import { ExternalLink, PlayCircle } from 'lucide-react';
import { useUser } from '../store/useAuthStore';

// Titles and video IDs verified against the BloomRx YouTube playlists.
// `roles` limits who a playlist is shown to; omitted means everyone.
const playlists = [
  {
    title: 'Physicians and Clinics',
    id: 'PLDcvwaxDVLm4',
    videos: [
      { title: 'Invitation and Settings Flow', id: '2zNQw67mp-4', duration: '2:29' },
      { title: 'NPI Setup Flow', id: 'XZMqngK73HI', duration: '1:26' },
      { title: 'Telehealth Network Sign Up', id: '45zzsI2CEy0', duration: '0:37' },
      { title: 'Placing Orders', id: 'K_qhnt0PMQw', duration: '3:30' },
    ],
  },
  {
    title: 'Account Managers',
    id: 'PLDRF9ZM55WX0',
    // Walkthroughs of managing a downline — adding AMs and physicians, building
    // catalogs, setting prices. A clinic has no such screens, so showing these
    // advertises tooling it cannot reach.
    roles: ['admin', 'supervisor', 'distributor'],
    videos: [
      { title: 'Bloom Intro', id: 'MQdPI7DNQNA', duration: '0:33' },
      { title: 'Dashboard Review', id: 'DA-OYPq98eY', duration: '1:20' },
      { title: 'Supervisor adding Account Managers', id: 'KkacrdvhEHs', duration: '0:54' },
      { title: 'Adding Physicians', id: 'tftjWnOmVjw', duration: '1:20' },
      { title: 'Managing Products', id: 'f24ubVQicKA', duration: '0:58' },
      { title: 'Managing Catalogs', id: 'PXRruMui3B4', duration: '2:01' },
      { title: 'Ordering Products', id: 'P414uQ4T2LU', duration: '2:18' },
    ],
  },
];

export default function VideoTutorials() {
  const user = useUser();
  const visiblePlaylists = playlists.filter(
    (playlist) => !playlist.roles || playlist.roles.includes(user?.role ?? '')
  );

  return (
    <div className="mx-auto max-w-6xl space-y-10">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Video Tutorials</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Short walkthroughs to help you get started and manage your work in Bloom. Videos open on YouTube in a new tab.
        </p>
      </header>
      {visiblePlaylists.map((playlist) => (
        <section key={playlist.id} aria-labelledby={playlist.id}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 id={playlist.id} className="text-xl font-semibold text-gray-900 dark:text-white">{playlist.title}</h2>
            <a href={`https://www.youtube.com/playlist?list=${playlist.id}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:underline dark:text-primary-400">
              View playlist <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {playlist.videos.map((video, index) => (
              <a key={video.id} href={`https://www.youtube.com/watch?v=${video.id}&list=${playlist.id}&index=${index + 1}`}
                target="_blank" rel="noopener noreferrer"
                className="group flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:border-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-400">
                <PlayCircle className="h-8 w-8 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                <div>
                  <h3 className="font-medium text-gray-900 group-hover:underline dark:text-white">{video.title}</h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{video.duration} · Watch on YouTube</p>
                </div>
              </a>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
